"use client";

import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { api, ApiError, type SlippagePrefs } from "@/lib/api";
import { Skeleton } from "@/components/Skeleton";

interface State {
  loading: boolean;
  strict: boolean;
  // STORY-817 — power-user "advanced order types" toggle. Default
  // false; when true the trade panel surfaces the
  // FOK/GTD/GTC/POSTONLY execution-flag selector.
  advancedOrderTypes: boolean;
  saving: boolean;
  error: string | null;
  // STORY-710 — slippage prefs co-loaded with the strict toggle.
  // Null while loading; populated from /v1/me/slippage-prefs.
  slippage: SlippagePrefs | null;
}

/**
 * STORY-348 — single-toggle trading preferences page. The setting is
 * "strict market orders": when ON, market orders that don't fully
 * fill cancel the remainder. When OFF (default), the backend
 * auto-converts the unfilled portion to a resting limit at the
 * outcome anchor price.
 *
 * The OFF default matches Polymarket's behaviour and is friendlier
 * to thin-book closed-beta liquidity. Power users who want explicit
 * cancel-on-no-fill turn this ON.
 */
export function TradingSettings() {
  const { t } = useTranslation();
  const [state, setState] = useState<State>({
    loading: true,
    strict: false,
    advancedOrderTypes: false,
    saving: false,
    error: null,
    slippage: null,
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // Co-load me + slippage so the page renders with both ready.
        // STORY-710's /slippage-prefs endpoint is independent of the
        // strict-mode read, so a Promise.all is the right shape.
        const [me, slippage] = await Promise.all([
          api.me(),
          api.getSlippagePrefs().catch(() => null),
        ]);
        if (!cancelled) {
          setState({
            loading: false,
            strict: me.strict_market_orders === true,
            advancedOrderTypes: me.advanced_order_types_enabled === true,
            saving: false,
            error: null,
            slippage,
          });
        }
      } catch (err) {
        if (cancelled) return;
        setState({
          loading: false,
          strict: false,
          advancedOrderTypes: false,
          saving: false,
          error:
            err instanceof Error
              ? err.message
              : t("tradingSettings.loadFailed"),
          slippage: null,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
    // `t` is stable per the react-i18next contract; including it
    // would re-fetch on every locale flip for no functional gain.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function saveSlippage(input: Partial<SlippagePrefs>) {
    setState((s) => ({ ...s, saving: true, error: null }));
    try {
      const updated = await api.updateSlippagePrefs(input);
      setState((s) => ({ ...s, slippage: updated, saving: false }));
    } catch (err) {
      setState((s) => ({
        ...s,
        saving: false,
        error:
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : t("tradingSettings.updateSlippageFailed"),
      }));
    }
  }

  async function handleToggle(next: boolean) {
    const previous = state.strict;
    setState((s) => ({ ...s, strict: next, saving: true, error: null }));
    try {
      const result = await api.updateStrictMarketOrders(next);
      setState((s) => ({
        ...s,
        strict: result.strict_market_orders,
        saving: false,
      }));
    } catch (err) {
      // Revert on failure so the UI doesn't lie about backend state.
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("tradingSettings.updateFailed");
      setState((s) => ({
        ...s,
        strict: previous,
        saving: false,
        error: message,
      }));
    }
  }

  // STORY-817 — same optimistic-flip + revert-on-failure shape as
  // handleToggle above. Distinct handler so a save in flight on
  // strict doesn't cross-cancel the advanced toggle (separate
  // saving-state would be cleaner but the existing UI already
  // disables both during any save; matching the simpler shape).
  async function handleAdvancedToggle(next: boolean) {
    const previous = state.advancedOrderTypes;
    setState((s) => ({
      ...s,
      advancedOrderTypes: next,
      saving: true,
      error: null,
    }));
    try {
      const result = await api.updateAdvancedOrderTypesEnabled(next);
      setState((s) => ({
        ...s,
        advancedOrderTypes: result.advancedOrderTypesEnabled,
        saving: false,
      }));
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("tradingSettings.updateFailed");
      setState((s) => ({
        ...s,
        advancedOrderTypes: previous,
        saving: false,
        error: message,
      }));
    }
  }

  if (state.loading) {
    return (
      <div
        role="status"
        aria-label={t("tradingSettings.loadingAria")}
        className="space-y-3"
      >
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-12 w-full rounded-md" />
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-12 w-full rounded-md" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* STORY-710 — Default slippage tolerance (cents). The trade
          panel pre-populates its Max-slippage input from this value;
          per-order override is always allowed there. */}
      {state.slippage ? (
        <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-4 py-3">
          <p className="text-[13px] font-bold text-[var(--color-text)]">
            {t("tradingSettings.defaultMaxSlippage")}
          </p>
          <p className="mt-1 text-[12px] text-[var(--color-text3)]">
            {t("tradingSettings.defaultMaxSlippageBody")}
          </p>
          <div className="mt-3 flex items-center gap-3">
            <input
              type="number"
              min={1}
              max={99}
              value={state.slippage.defaultMaxSlippageCents}
              disabled={state.saving}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (Number.isFinite(v) && v >= 1 && v <= 99) {
                  setState((s) => ({
                    ...s,
                    slippage: s.slippage
                      ? { ...s.slippage, defaultMaxSlippageCents: v }
                      : s.slippage,
                  }));
                }
              }}
              onBlur={(e) => {
                const v = parseInt(e.target.value, 10);
                if (Number.isFinite(v) && v >= 1 && v <= 99) {
                  void saveSlippage({ defaultMaxSlippageCents: v });
                }
              }}
              className="w-20 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-1.5 font-mono text-[13px] tabular-nums text-[var(--color-text)] outline-none focus:border-[var(--color-brand)]"
            />
            <span className="text-[12px] text-[var(--color-text3)]">
              {t("tradingSettings.centsFromAnchor")}
            </span>
          </div>
        </div>
      ) : null}

      {/* STORY-711 — Auto-convert behaviour on slippage. Distinct from
          strict-mode below: this only changes what happens when
          slippage was the binding constraint. Strict-mode kills
          auto-convert for ALL market remainders regardless of cause. */}
      {state.slippage ? (
        <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-4 py-3">
          <p className="text-[13px] font-bold text-[var(--color-text)]">
            {t("tradingSettings.whenSlippageRejects")}
          </p>
          <p className="mt-1 text-[12px] text-[var(--color-text3)]">
            <Trans
              i18nKey="tradingSettings.whenSlippageRejectsBody1"
              components={{ strong: <strong /> }}
            />{" "}
            <Trans
              i18nKey="tradingSettings.whenSlippageRejectsBody2"
              components={{ strong: <strong /> }}
            />
          </p>
          <div className="mt-3 flex gap-2">
            {(["auto-convert", "reject"] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                disabled={state.saving}
                onClick={() => void saveSlippage({ slippageAutoConvert: opt })}
                className="rounded-md border px-3 py-1.5 text-[12px] font-semibold transition-colors disabled:opacity-50"
                style={{
                  borderColor:
                    state.slippage?.slippageAutoConvert === opt
                      ? "var(--color-brand)"
                      : "var(--color-border)",
                  background:
                    state.slippage?.slippageAutoConvert === opt
                      ? "color-mix(in srgb, var(--color-brand) 12%, transparent)"
                      : "var(--color-bg)",
                  color:
                    state.slippage?.slippageAutoConvert === opt
                      ? "var(--color-brand)"
                      : "var(--color-text2)",
                }}
              >
                {opt === "auto-convert"
                  ? t("tradingSettings.autoConvert")
                  : t("tradingSettings.reject")}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <label className="flex items-start justify-between gap-4 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-4 py-3">
        <div className="min-w-0">
          <p className="text-[13px] font-bold text-[var(--color-text)]">
            {t("tradingSettings.strictMarketOrders")}
          </p>
          <p className="mt-1 text-[12px] text-[var(--color-text3)]">
            {t("tradingSettings.strictMarketOrdersBody")}
          </p>
        </div>
        {/* Custom toggle — the native checkbox's `accent-color`
            paints the box in the brand green when checked, which
            reads as "approved / recommended". Strict mode isn't
            either of those — it's a deliberate opt-in to
            cancel-on-no-fill behaviour. Use a neutral slate when
            ON instead. Visually-hidden checkbox keeps the
            accessible label + native focus/keyboard behaviour. */}
        <button
          type="button"
          role="switch"
          aria-checked={state.strict}
          aria-label={t("tradingSettings.strictMarketOrdersAria")}
          disabled={state.saving}
          onClick={() => void handleToggle(!state.strict)}
          className="relative mt-1 inline-flex h-5 w-9 flex-shrink-0 cursor-pointer items-center rounded-full border border-[var(--color-border)] transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          style={{
            background: state.strict
              ? "var(--color-text2)"
              : "var(--color-bg)",
          }}
        >
          <span
            className="inline-block h-3.5 w-3.5 transform rounded-full bg-[var(--color-text)] shadow-sm transition-transform"
            style={{
              transform: state.strict ? "translateX(18px)" : "translateX(2px)",
            }}
          />
        </button>
      </label>

      {/* STORY-817 — power-user "show advanced order types" toggle.
          When OFF (default) the trade panel uses the smart-routing
          FAK default that the regular user surface needs. When ON
          the trade panel surfaces FOK/GTD/GTC/POSTONLY selectors
          for power users who explicitly opt in. Pure UI affordance —
          the matcher already supports these flags. Trade-panel
          render-side wiring lands in a follow-up PR. */}
      <label className="flex items-start justify-between gap-4 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-4 py-3">
        <div className="min-w-0">
          <p className="text-[13px] font-bold text-[var(--color-text)]">
            {t(
              "tradingSettings.advancedOrderTypes",
              "Show advanced order types",
            )}
          </p>
          <p className="mt-1 text-[12px] text-[var(--color-text3)]">
            {t(
              "tradingSettings.advancedOrderTypesBody",
              "Power-user: surface Fill-Or-Kill, Good-Till-Date, Good-Till-Cancelled, and Post-Only execution flags in the trade panel. Off by default; the smart-routing default handles 99%+ of trades.",
            )}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={state.advancedOrderTypes}
          aria-label={t(
            "tradingSettings.advancedOrderTypesAria",
            "Toggle advanced order types",
          )}
          disabled={state.saving}
          onClick={() =>
            void handleAdvancedToggle(!state.advancedOrderTypes)
          }
          className="relative mt-1 inline-flex h-5 w-9 flex-shrink-0 cursor-pointer items-center rounded-full border border-[var(--color-border)] transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          style={{
            background: state.advancedOrderTypes
              ? "var(--color-text2)"
              : "var(--color-bg)",
          }}
        >
          <span
            className="inline-block h-3.5 w-3.5 transform rounded-full bg-[var(--color-text)] shadow-sm transition-transform"
            style={{
              transform: state.advancedOrderTypes
                ? "translateX(18px)"
                : "translateX(2px)",
            }}
          />
        </button>
      </label>

      {state.error && (
        <p role="alert" className="text-[12px] text-[var(--color-no)]">
          {state.error}
        </p>
      )}
      {state.saving && (
        <p
          role="status"
          className="text-[12px] text-[var(--color-text3)]"
          aria-live="polite"
        >
          {t("tradingSettings.saving")}
        </p>
      )}
    </div>
  );
}
